export class EvoluaApiError extends Error {
    status;
    problem;
    constructor(problem) {
        super(problem.detail || problem.title);
        this.name = "EvoluaApiError";
        this.status = problem.status;
        this.problem = problem;
    }
}
/** Cold start Render free — tempo máximo por request HTTP. */
export const API_REQUEST_TIMEOUT_MS = 45_000;
export function isFetchAbortError(err) {
    return (err instanceof Error && (err.name === "AbortError" || err.name === "TimeoutError"));
}
export function networkProblemDetails(code, detail) {
    return {
        type: `https://evolua.hcm/errors/${code.toLowerCase().replace("_", "-")}`,
        title: code === "TIMEOUT" ? "Timeout" : "Network Error",
        status: 0,
        detail,
        code,
    };
}
/** Converte falha de fetch (abort/rede) em EvoluaApiError tipado. */
export function mapFetchFailureToApiError(err) {
    if (isFetchAbortError(err)) {
        return new EvoluaApiError(networkProblemDetails("TIMEOUT", "A API demorou para responder. Em ambiente free ela pode estar iniciando — aguarde e tente de novo."));
    }
    return new EvoluaApiError(networkProblemDetails("NETWORK_ERROR", "Falha de rede ao falar com a API. Verifique a conexão ou se o serviço está no ar."));
}
function resolveRequestSignal(userSignal, timeoutMs) {
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    if (!userSignal)
        return timeoutSignal;
    const anyFn = AbortSignal.any;
    if (typeof anyFn === "function") {
        return anyFn([userSignal, timeoutSignal]);
    }
    return timeoutSignal;
}
export class EvoluaApiClient {
    baseUrl;
    getAccessToken;
    getTenantId;
    fetchImpl;
    requestTimeoutMs;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, "");
        this.getAccessToken = options.getAccessToken;
        this.getTenantId = options.getTenantId;
        this.fetchImpl = options.fetchImpl ?? fetch;
        this.requestTimeoutMs = options.requestTimeoutMs ?? API_REQUEST_TIMEOUT_MS;
    }
    async health() {
        return this.requestJson("/health");
    }
    async listEmployees(query) {
        const qs = buildEmployeeQuery(query).toString();
        return this.requestJson(`/v1/employees${qs ? `?${qs}` : ""}`);
    }
    async exportEmployees(query) {
        const qs = buildEmployeeQuery(query).toString();
        const headers = {
            Accept: "text/csv",
            ...(await this.authHeaders()),
        };
        const response = await this.fetchImpl(`${this.baseUrl}/v1/employees/export${qs ? `?${qs}` : ""}`, { method: "POST", headers });
        if (!response.ok) {
            let problem;
            try {
                problem = (await response.json());
            }
            catch {
                problem = {
                    type: "https://evolua.hcm/errors/http",
                    title: "HTTP Error",
                    status: response.status,
                    detail: `Falha HTTP ${response.status}`,
                    code: "HTTP_ERROR",
                };
            }
            throw new EvoluaApiError(problem);
        }
        const csv = await response.text();
        const disposition = response.headers.get("Content-Disposition") ?? "";
        const match = /filename="([^"]+)"/.exec(disposition);
        const filename = match?.[1] ?? "colaboradores.csv";
        const auditId = response.headers.get("X-Audit-Id") ?? "";
        const rowCount = Number.parseInt(response.headers.get("X-Row-Count") ?? "0", 10);
        return {
            csv,
            filename,
            auditId,
            rowCount: Number.isFinite(rowCount) ? rowCount : 0,
        };
    }
    async getEmployee(id) {
        return this.requestJson(`/v1/employees/${id}`);
    }
    async getEmployeeCareerHistory(id) {
        return this.requestJson(`/v1/employees/${encodeURIComponent(id)}/career-history`);
    }
    async revealEmployeeField(id, field) {
        return this.requestJson(`/v1/employees/${encodeURIComponent(id)}/reveal`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ field }),
        });
    }
    async patchEmployee(id, body) {
        return this.requestJson(`/v1/employees/${encodeURIComponent(id)}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async createEmployeeNote(employeeId, body, idempotencyKey) {
        return this.requestJson(`/v1/employees/${employeeId}/notes`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": idempotencyKey,
            },
            body: JSON.stringify(body),
        });
    }
    async getFeatureFlags() {
        return this.requestJson("/v1/feature-flags");
    }
    async setFeatureFlag(key, body) {
        return this.requestJson(`/v1/feature-flags/${encodeURIComponent(key)}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listFeatureFlagAudit(key) {
        const qs = key ? `?key=${encodeURIComponent(key)}` : "";
        return this.requestJson(`/v1/feature-flags/audit${qs}`);
    }
    async listCompanies() {
        return this.requestJson("/v1/companies");
    }
    async getCompany(id) {
        return this.requestJson(`/v1/companies/${encodeURIComponent(id)}`);
    }
    async listDepartments(companyId) {
        const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
        return this.requestJson(`/v1/departments${qs}`);
    }
    async getDepartmentTree(companyId) {
        const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
        return this.requestJson(`/v1/departments/tree${qs}`);
    }
    async getOrgChart(query = {}) {
        const params = new URLSearchParams();
        if (query.parentId)
            params.set("parentId", query.parentId);
        if (query.focusId)
            params.set("focusId", query.focusId);
        if (query.q)
            params.set("q", query.q);
        if (query.maxDepth !== undefined)
            params.set("maxDepth", String(query.maxDepth));
        const qs = params.toString();
        return this.requestJson(`/v1/orgchart${qs ? `?${qs}` : ""}`);
    }
    async getOrgChartPending() {
        return this.requestJson("/v1/orgchart/pending");
    }
    async getDepartment(id) {
        return this.requestJson(`/v1/departments/${encodeURIComponent(id)}`);
    }
    async listPositions() {
        return this.requestJson("/v1/positions");
    }
    async getPosition(id) {
        return this.requestJson(`/v1/positions/${encodeURIComponent(id)}`);
    }
    async listIntegrations() {
        return this.requestJson("/v1/integrations");
    }
    async getIntegration(id) {
        return this.requestJson(`/v1/integrations/${id}`);
    }
    async createIntegration(body, idempotencyKey) {
        return this.requestJson("/v1/integrations", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Idempotency-Key": idempotencyKey,
            },
            body: JSON.stringify(body),
        });
    }
    async updateIntegration(id, body) {
        return this.requestJson(`/v1/integrations/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async disableIntegration(id) {
        return this.requestJson(`/v1/integrations/${id}/disable`, {
            method: "POST",
        });
    }
    async getMeDashboard() {
        return this.requestJson("/v1/me/dashboard");
    }
    async getManagerDashboard() {
        return this.requestJson("/v1/manager/dashboard");
    }
    async listManagerApprovals() {
        return this.requestJson("/v1/manager/approvals");
    }
    async approveManagerApproval(id, body = {}) {
        return this.requestJson(`/v1/manager/approvals/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getManagerVacationCalendar(query = {}) {
        const params = new URLSearchParams();
        if (query.from)
            params.set("from", query.from);
        if (query.to)
            params.set("to", query.to);
        if (query.view)
            params.set("view", query.view);
        if (query.minPresentPercent !== undefined) {
            params.set("minPresentPercent", String(query.minPresentPercent));
        }
        const qs = params.toString();
        return this.requestJson(`/v1/manager/vacation-calendar${qs ? `?${qs}` : ""}`);
    }
    async getManagerTeamIndicators(query = {}) {
        const params = new URLSearchParams();
        if (query.from)
            params.set("from", query.from);
        if (query.period)
            params.set("period", query.period);
        const qs = params.toString();
        return this.requestJson(`/v1/manager/team-indicators${qs ? `?${qs}` : ""}`);
    }
    async rejectManagerApproval(id, body) {
        return this.requestJson(`/v1/manager/approvals/${encodeURIComponent(id)}/reject`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async batchManagerApprovals(body) {
        return this.requestJson("/v1/manager/approvals/batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async syncManagerVacations() {
        return this.requestJson("/v1/manager/approvals/sync-vacations", { method: "POST" });
    }
    async listManagerTeam(query = {}) {
        const params = new URLSearchParams();
        if (query.q)
            params.set("q", query.q);
        if (query.status)
            params.set("status", query.status);
        if (query.relation)
            params.set("relation", query.relation);
        if (query.includeTerminated !== undefined) {
            params.set("includeTerminated", String(query.includeTerminated));
        }
        const qs = params.toString();
        return this.requestJson(`/v1/manager/team${qs ? `?${qs}` : ""}`);
    }
    async getManagerTeamMember(employeeId) {
        return this.requestJson(`/v1/manager/team/${encodeURIComponent(employeeId)}`);
    }
    async listHrRequests(query = {}) {
        const params = new URLSearchParams();
        if (query.type)
            params.set("type", query.type);
        if (query.status)
            params.set("status", query.status);
        if (query.assignee)
            params.set("assignee", query.assignee);
        if (query.overdue !== undefined)
            params.set("overdue", String(query.overdue));
        if (query.q)
            params.set("q", query.q);
        if (query.dueBefore)
            params.set("dueBefore", query.dueBefore);
        if (query.dueAfter)
            params.set("dueAfter", query.dueAfter);
        const qs = params.toString();
        return this.requestJson(`/v1/hr/requests${qs ? `?${qs}` : ""}`);
    }
    async getHrRequest(id) {
        return this.requestJson(`/v1/hr/requests/${encodeURIComponent(id)}`);
    }
    async getHrRequestMetrics() {
        return this.requestJson("/v1/hr/requests/metrics");
    }
    async assignHrRequest(id, body = {}) {
        return this.requestJson(`/v1/hr/requests/${encodeURIComponent(id)}/assign`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async reassignHrRequest(id, body) {
        return this.requestJson(`/v1/hr/requests/${encodeURIComponent(id)}/reassign`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async respondHrRequest(id, body) {
        return this.requestJson(`/v1/hr/requests/${encodeURIComponent(id)}/respond`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async approveHrRequest(id, body = {}) {
        return this.requestJson(`/v1/hr/requests/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async rejectHrRequest(id, body) {
        return this.requestJson(`/v1/hr/requests/${encodeURIComponent(id)}/reject`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listHrVacations(query = {}) {
        const params = new URLSearchParams();
        if (query.status)
            params.set("status", query.status);
        if (query.companyId)
            params.set("companyId", query.companyId);
        if (query.departmentId)
            params.set("departmentId", query.departmentId);
        if (query.q)
            params.set("q", query.q);
        if (query.from)
            params.set("from", query.from);
        if (query.to)
            params.set("to", query.to);
        const qs = params.toString();
        return this.requestJson(`/v1/hr/vacations${qs ? `?${qs}` : ""}`);
    }
    async approveHrVacationOnBehalf(id, body) {
        return this.requestJson(`/v1/hr/vacations/${encodeURIComponent(id)}/approve-on-behalf`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listHrDocuments(query = {}) {
        const params = new URLSearchParams();
        if (query.q)
            params.set("q", query.q);
        if (query.category)
            params.set("category", query.category);
        if (query.departmentId)
            params.set("departmentId", query.departmentId);
        if (query.employeeId)
            params.set("employeeId", query.employeeId);
        if (query.expired !== undefined)
            params.set("expired", String(query.expired));
        const qs = params.toString();
        return this.requestJson(`/v1/hr/documents${qs ? `?${qs}` : ""}`);
    }
    async createHrDocument(body) {
        return this.requestJson("/v1/hr/documents", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async batchHrDocuments(body) {
        return this.requestJson("/v1/hr/documents/batch", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getHrDocument(id) {
        return this.requestJson(`/v1/hr/documents/${encodeURIComponent(id)}`);
    }
    async distributeHrDocument(id, body) {
        return this.requestJson(`/v1/hr/documents/${encodeURIComponent(id)}/distribute`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listHrDocumentAccesses(id) {
        return this.requestJson(`/v1/hr/documents/${encodeURIComponent(id)}/accesses`);
    }
    async listHrAnnouncements(query = {}) {
        const params = new URLSearchParams();
        if (query.status)
            params.set("status", query.status);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/hr/announcements${qs ? `?${qs}` : ""}`);
    }
    async createHrAnnouncement(body) {
        return this.requestJson("/v1/hr/announcements", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getHrAnnouncement(id) {
        return this.requestJson(`/v1/hr/announcements/${encodeURIComponent(id)}`);
    }
    async publishHrAnnouncement(id) {
        return this.requestJson(`/v1/hr/announcements/${encodeURIComponent(id)}/publish`, { method: "POST" });
    }
    async getHrAnnouncementReach(id) {
        return this.requestJson(`/v1/hr/announcements/${encodeURIComponent(id)}/reach`);
    }
    async listHrAdmissions(query = {}) {
        const params = new URLSearchParams();
        if (query.status)
            params.set("status", query.status);
        if (query.companyId)
            params.set("companyId", query.companyId);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/hr/admissions${qs ? `?${qs}` : ""}`);
    }
    async createHrAdmission(body) {
        return this.requestJson("/v1/hr/admissions", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getHrAdmission(id) {
        return this.requestJson(`/v1/hr/admissions/${encodeURIComponent(id)}`);
    }
    async validateHrAdmissionItem(id, key, body) {
        return this.requestJson(`/v1/hr/admissions/${encodeURIComponent(id)}/items/${encodeURIComponent(key)}/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async handoffHrAdmission(id) {
        return this.requestJson(`/v1/hr/admissions/${encodeURIComponent(id)}/handoff`, { method: "POST" });
    }
    async getAdmissionInvite(token) {
        return this.requestJson(`/v1/admissions/invite/${encodeURIComponent(token)}`);
    }
    async submitAdmissionInviteItem(token, key, body) {
        return this.requestJson(`/v1/admissions/invite/${encodeURIComponent(token)}/items/${encodeURIComponent(key)}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listHrOffboardings(query = {}) {
        const params = new URLSearchParams();
        if (query.status)
            params.set("status", query.status);
        if (query.companyId)
            params.set("companyId", query.companyId);
        if (query.employeeId)
            params.set("employeeId", query.employeeId);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/hr/offboardings${qs ? `?${qs}` : ""}`);
    }
    async createHrOffboarding(body) {
        return this.requestJson("/v1/hr/offboardings", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getHrOffboarding(id) {
        return this.requestJson(`/v1/hr/offboardings/${encodeURIComponent(id)}`);
    }
    async completeHrOffboardingItem(id, key, body = {}) {
        return this.requestJson(`/v1/hr/offboardings/${encodeURIComponent(id)}/items/${encodeURIComponent(key)}/complete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async returnHrOffboardingAsset(id, assetId, body = {}) {
        return this.requestJson(`/v1/hr/offboardings/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}/return`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async saveHrOffboardingInterview(id, body) {
        return this.requestJson(`/v1/hr/offboardings/${encodeURIComponent(id)}/interview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async revokeHrOffboardingAccess(id) {
        return this.requestJson(`/v1/hr/offboardings/${encodeURIComponent(id)}/revoke-access`, { method: "POST" });
    }
    async completeHrOffboarding(id) {
        return this.requestJson(`/v1/hr/offboardings/${encodeURIComponent(id)}/complete`, { method: "POST" });
    }
    async listAdminTenants(query = {}) {
        const params = new URLSearchParams();
        if (query.status)
            params.set("status", query.status);
        if (query.planId)
            params.set("planId", query.planId);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/admin/tenants${qs ? `?${qs}` : ""}`);
    }
    async createAdminTenant(body) {
        return this.requestJson("/v1/admin/tenants", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getAdminTenant(id) {
        return this.requestJson(`/v1/admin/tenants/${encodeURIComponent(id)}`);
    }
    async suspendAdminTenant(id, body) {
        return this.requestJson(`/v1/admin/tenants/${encodeURIComponent(id)}/suspend`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async reactivateAdminTenant(id, body) {
        return this.requestJson(`/v1/admin/tenants/${encodeURIComponent(id)}/reactivate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listIntegrationsConsole(query = {}) {
        const params = new URLSearchParams();
        if (query.status)
            params.set("status", query.status);
        if (query.tenantId)
            params.set("tenantId", query.tenantId);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/admin/integrations-console${qs ? `?${qs}` : ""}`);
    }
    async getIntegrationsConsoleRow(integrationId) {
        return this.requestJson(`/v1/admin/integrations-console/${encodeURIComponent(integrationId)}`);
    }
    async simulateIntegrationsConsoleStatus(integrationId, body) {
        return this.requestJson(`/v1/admin/integrations-console/${encodeURIComponent(integrationId)}/simulate-status`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listAdminSyncRuns(query = {}) {
        const params = new URLSearchParams();
        if (query.tenantId)
            params.set("tenantId", query.tenantId);
        if (query.entityType)
            params.set("entityType", query.entityType);
        if (query.status)
            params.set("status", query.status);
        if (query.errorCategory)
            params.set("errorCategory", query.errorCategory);
        if (query.from)
            params.set("from", query.from);
        if (query.to)
            params.set("to", query.to);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/admin/sync-runs${qs ? `?${qs}` : ""}`);
    }
    async getAdminSyncRun(id) {
        return this.requestJson(`/v1/admin/sync-runs/${encodeURIComponent(id)}`);
    }
    async listAdminSyncDlq() {
        return this.requestJson("/v1/admin/sync-runs/dlq");
    }
    async reprocessAdminSyncRun(id, body) {
        return this.requestJson(`/v1/admin/sync-runs/${encodeURIComponent(id)}/reprocess`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async reprocessAdminSyncDlq(dlqId, body) {
        return this.requestJson(`/v1/admin/sync-runs/dlq/${encodeURIComponent(dlqId)}/reprocess`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async searchAdminUsers(body) {
        return this.requestJson("/v1/admin/users/search", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async openAdminImpersonation(body) {
        return this.requestJson("/v1/admin/impersonation", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getAdminImpersonation(id) {
        return this.requestJson(`/v1/admin/impersonation/${encodeURIComponent(id)}`);
    }
    async approveAdminImpersonation(id, body) {
        return this.requestJson(`/v1/admin/impersonation/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async endAdminImpersonation(id) {
        return this.requestJson(`/v1/admin/impersonation/${encodeURIComponent(id)}/end`, { method: "POST" });
    }
    async actAdminImpersonation(id, body) {
        return this.requestJson(`/v1/admin/impersonation/${encodeURIComponent(id)}/actions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listAdminImpersonationAudit(id) {
        return this.requestJson(`/v1/admin/impersonation/${encodeURIComponent(id)}/audit`);
    }
    async getAdminOpsStatus() {
        return this.requestJson("/v1/admin/ops/status");
    }
    async listAdminOpsErrors(query = {}) {
        const params = new URLSearchParams();
        if (query.tenantId)
            params.set("tenantId", query.tenantId);
        if (query.type)
            params.set("type", query.type);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/admin/ops/errors${qs ? `?${qs}` : ""}`);
    }
    async simulateAdminOpsOutage(body) {
        return this.requestJson("/v1/admin/ops/simulate-outage", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async ingestAdminOpsError(body) {
        return this.requestJson("/v1/admin/ops/errors", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listAdminAudit(query = {}) {
        const params = new URLSearchParams();
        if (query.actorSubject)
            params.set("actorSubject", query.actorSubject);
        if (query.tenantId)
            params.set("tenantId", query.tenantId);
        if (query.action)
            params.set("action", query.action);
        if (query.from)
            params.set("from", query.from);
        if (query.to)
            params.set("to", query.to);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/admin/audit${qs ? `?${qs}` : ""}`);
    }
    async exportAdminAudit(query = {}) {
        const params = new URLSearchParams();
        if (query.actorSubject)
            params.set("actorSubject", query.actorSubject);
        if (query.tenantId)
            params.set("tenantId", query.tenantId);
        if (query.action)
            params.set("action", query.action);
        if (query.from)
            params.set("from", query.from);
        if (query.to)
            params.set("to", query.to);
        if (query.q)
            params.set("q", query.q);
        const qs = params.toString();
        return this.requestJson(`/v1/admin/audit/export${qs ? `?${qs}` : ""}`);
    }
    async listAdminAuditReports() {
        return this.requestJson("/v1/admin/audit/reports");
    }
    async getAdminAuditReport(id) {
        return this.requestJson(`/v1/admin/audit/reports/${encodeURIComponent(id)}`);
    }
    async generateAdminAuditReport(body = {}) {
        return this.requestJson("/v1/admin/audit/reports/generate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getMeProfile() {
        return this.requestJson("/v1/me/profile");
    }
    async patchMeProfile(body) {
        return this.requestJson("/v1/me/profile", {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async revealMeField(field) {
        return this.requestJson("/v1/me/profile/reveal", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ field }),
        });
    }
    async requestMeCorrection(body) {
        return this.requestJson("/v1/me/profile/correction-request", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listMeCorrections() {
        return this.requestJson("/v1/me/profile/corrections");
    }
    async listMePayslips() {
        return this.requestJson("/v1/me/payslips");
    }
    async getMePayslip(id) {
        return this.requestJson(`/v1/me/payslips/${encodeURIComponent(id)}`);
    }
    async downloadMePayslip(id) {
        const headers = {
            Accept: "text/plain",
            ...(await this.authHeaders()),
        };
        const response = await this.fetchImpl(`${this.baseUrl}/v1/me/payslips/${encodeURIComponent(id)}/download`, { method: "GET", headers });
        if (!response.ok) {
            let problem;
            try {
                problem = (await response.json());
            }
            catch {
                problem = {
                    type: "https://evolua.hcm/errors/http",
                    title: "HTTP Error",
                    status: response.status,
                    detail: `Falha HTTP ${response.status}`,
                    code: "HTTP_ERROR",
                };
            }
            throw new EvoluaApiError(problem);
        }
        const content = await response.text();
        const disposition = response.headers.get("Content-Disposition") ?? "";
        const match = /filename="([^"]+)"/.exec(disposition);
        const filename = match?.[1] ?? "holerite.txt";
        const auditId = response.headers.get("X-Audit-Id") ?? "";
        return { filename, content, auditId };
    }
    async listMeBenefits() {
        return this.requestJson("/v1/me/benefits");
    }
    async getMeBenefit(id) {
        return this.requestJson(`/v1/me/benefits/${encodeURIComponent(id)}`);
    }
    async listMeDocuments(query = {}) {
        const params = new URLSearchParams();
        if (query.q)
            params.set("q", query.q);
        if (query.category)
            params.set("category", query.category);
        const qs = params.toString();
        return this.requestJson(`/v1/me/documents${qs ? `?${qs}` : ""}`);
    }
    async getMeDocument(id) {
        return this.requestJson(`/v1/me/documents/${encodeURIComponent(id)}`);
    }
    async listMeAnnouncements() {
        return this.requestJson("/v1/me/announcements");
    }
    async getMeAnnouncement(id) {
        return this.requestJson(`/v1/me/announcements/${encodeURIComponent(id)}`);
    }
    async ackMeAnnouncement(id) {
        return this.requestJson(`/v1/me/announcements/${encodeURIComponent(id)}/ack`, { method: "POST" });
    }
    async listMeTimecards(query = {}) {
        const params = new URLSearchParams();
        if (query.from)
            params.set("from", query.from);
        if (query.to)
            params.set("to", query.to);
        const qs = params.toString();
        return this.requestJson(`/v1/me/timecards${qs ? `?${qs}` : ""}`);
    }
    async getMeTimecardBalance() {
        return this.requestJson("/v1/me/timecards/balance");
    }
    async createMeDocumentDownload(id) {
        return this.requestJson(`/v1/me/documents/${encodeURIComponent(id)}/download`);
    }
    async downloadMeDocumentContent(downloadUrl) {
        const headers = {
            Accept: "text/plain",
            ...(await this.authHeaders()),
        };
        const url = downloadUrl.startsWith("http")
            ? downloadUrl
            : `${this.baseUrl}${downloadUrl}`;
        const response = await this.fetchImpl(url, { method: "GET", headers });
        if (!response.ok) {
            let problem;
            try {
                problem = (await response.json());
            }
            catch {
                problem = {
                    type: "https://evolua.hcm/errors/http",
                    title: "HTTP Error",
                    status: response.status,
                    detail: `Falha HTTP ${response.status}`,
                    code: "HTTP_ERROR",
                };
            }
            throw new EvoluaApiError(problem);
        }
        const content = await response.text();
        const disposition = response.headers.get("Content-Disposition") ?? "";
        const match = /filename="([^"]+)"/.exec(disposition);
        const filename = match?.[1] ?? "documento.txt";
        return { filename, content };
    }
    async getMeVacationBalance() {
        return this.requestJson("/v1/me/vacations/balance");
    }
    async getMeVacationHistory() {
        return this.requestJson("/v1/me/vacations/history");
    }
    async requestMeVacation(body) {
        return this.requestJson("/v1/me/vacations/requests", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listMeVacationRequests() {
        return this.requestJson("/v1/me/vacations/requests");
    }
    async cancelMeVacationRequest(id) {
        return this.requestJson(`/v1/me/vacations/requests/${encodeURIComponent(id)}/cancel`, { method: "POST" });
    }
    async listMeNotifications(query) {
        const params = new URLSearchParams();
        if (query?.unreadOnly)
            params.set("unreadOnly", "true");
        if (query?.type)
            params.set("type", query.type);
        const qs = params.toString();
        return this.requestJson(`/v1/me/notifications${qs ? `?${qs}` : ""}`);
    }
    async getMeNotificationsUnreadCount() {
        return this.requestJson("/v1/me/notifications/unread-count");
    }
    async markMeNotificationRead(id) {
        return this.requestJson(`/v1/me/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
    }
    async markAllMeNotificationsRead() {
        return this.requestJson("/v1/me/notifications/read-all", {
            method: "POST",
        });
    }
    async getMeNotificationPreferences() {
        return this.requestJson("/v1/me/notification-preferences");
    }
    async putMeNotificationPreferences(body) {
        return this.requestJson("/v1/me/notification-preferences", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listMePushDeliveries() {
        return this.requestJson("/v1/me/notifications/push-deliveries");
    }
    async requestMeDataExport() {
        return this.requestJson("/v1/me/data-exports", {
            method: "POST",
        });
    }
    async listMeDataExports() {
        return this.requestJson("/v1/me/data-exports");
    }
    async getMeDataExport(id) {
        return this.requestJson(`/v1/me/data-exports/${encodeURIComponent(id)}`);
    }
    async downloadMeDataExport(id) {
        const headers = {
            Accept: "application/json",
            ...(await this.authHeaders()),
        };
        const response = await this.fetchImpl(`${this.baseUrl}/v1/me/data-exports/${encodeURIComponent(id)}/download`, { method: "GET", headers });
        if (!response.ok) {
            let problem;
            try {
                problem = (await response.json());
            }
            catch {
                problem = {
                    type: "https://evolua.hcm/errors/http",
                    title: "HTTP Error",
                    status: response.status,
                    detail: `Falha HTTP ${response.status}`,
                    code: "HTTP_ERROR",
                };
            }
            throw new EvoluaApiError(problem);
        }
        const content = await response.text();
        const disposition = response.headers.get("Content-Disposition") ?? "";
        const match = /filename="([^"]+)"/.exec(disposition);
        const filename = match?.[1] ?? "evolua-lgpd-export.json";
        const auditId = response.headers.get("X-Audit-Id") ?? "";
        const expiresAt = response.headers.get("X-Export-Expires-At") ?? new Date().toISOString();
        return { filename, content, auditId, expiresAt };
    }
    async createMeRequest(body) {
        return this.requestJson("/v1/me/requests", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listMeRequests() {
        return this.requestJson("/v1/me/requests");
    }
    async getMeRequest(id) {
        return this.requestJson(`/v1/me/requests/${encodeURIComponent(id)}`);
    }
    async addMeRequestMessage(id, body) {
        return this.requestJson(`/v1/me/requests/${encodeURIComponent(id)}/messages`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async listWorkflowDefinitions() {
        return this.requestJson("/v1/workflows/definitions");
    }
    async startWorkflowInstance(body) {
        return this.requestJson("/v1/workflows/instances", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async getWorkflowInstance(id) {
        return this.requestJson(`/v1/workflows/instances/${encodeURIComponent(id)}`);
    }
    async approveWorkflowStep(id, body = {}) {
        return this.requestJson(`/v1/workflows/instances/${encodeURIComponent(id)}/approve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async rejectWorkflowStep(id, body = {}) {
        return this.requestJson(`/v1/workflows/instances/${encodeURIComponent(id)}/reject`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async delegateWorkflowStep(id, body) {
        return this.requestJson(`/v1/workflows/instances/${encodeURIComponent(id)}/delegate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async tickWorkflows(body = {}) {
        return this.requestJson("/v1/workflows/tick", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    }
    async requestJson(path, init = {}) {
        const headers = {
            Accept: "application/json",
            ...(await this.authHeaders()),
            ...init.headers,
        };
        const { signal: userSignal, ...restInit } = init;
        const signal = resolveRequestSignal(userSignal, this.requestTimeoutMs);
        let response;
        try {
            response = await this.fetchImpl(`${this.baseUrl}${path}`, {
                ...restInit,
                headers,
                signal,
            });
        }
        catch (err) {
            if (err instanceof EvoluaApiError)
                throw err;
            throw mapFetchFailureToApiError(err);
        }
        if (!response.ok) {
            let problem;
            try {
                problem = (await response.json());
            }
            catch {
                problem = {
                    type: "https://evolua.hcm/errors/http",
                    title: "HTTP Error",
                    status: response.status,
                    detail: `Falha HTTP ${response.status}`,
                    code: "HTTP_ERROR",
                };
            }
            throw new EvoluaApiError(problem);
        }
        return (await response.json());
    }
    async authHeaders() {
        const headers = {};
        const token = await this.getAccessToken?.();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const tenantId = await this.getTenantId?.();
        if (tenantId) {
            headers["x-tenant-id"] = tenantId;
        }
        return headers;
    }
}
function buildEmployeeQuery(query) {
    const params = new URLSearchParams();
    if (!query)
        return params;
    if (query.cursor)
        params.set("cursor", query.cursor);
    if (query.limit !== undefined)
        params.set("limit", String(query.limit));
    if (query.sort)
        params.set("sort", query.sort);
    if (query.status)
        params.set("filter[status]", query.status);
    if (query.companyId)
        params.set("filter[companyId]", query.companyId);
    if (query.departmentId)
        params.set("filter[departmentId]", query.departmentId);
    if (query.positionId)
        params.set("filter[positionId]", query.positionId);
    if (query.managerId)
        params.set("filter[managerId]", query.managerId);
    if (query.q)
        params.set("filter[q]", query.q);
    return params;
}
//# sourceMappingURL=index.js.map